<script setup>
import { ref, computed } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';
import ContactsCard from 'dashboard/components-next/Contacts/ContactsCard/ContactsCard.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

const props = defineProps({
  contacts: { type: Array, required: true },
  selectedContactIds: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['toggleContact', 'toggleAll']);

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const route = useRoute();

const uiFlags = useMapGetter('contacts/getUIFlags');
const isUpdating = computed(() => uiFlags.value.isUpdating);
const expandedCardId = ref(null);

const selectedIdsSet = computed(() => new Set(props.selectedContactIds || []));

const updateContact = async updatedData => {
  try {
    await store.dispatch('contacts/update', updatedData);
    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.SUCCESS_MESSAGE'));
  } catch (error) {
    const i18nPrefix = 'CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM';
    if (error instanceof DuplicateContactException) {
      if (error.data.includes('email')) {
        useAlert(t(`${i18nPrefix}.EMAIL_ADDRESS.DUPLICATE`));
      } else if (error.data.includes('phone_number')) {
        useAlert(t(`${i18nPrefix}.PHONE_NUMBER.DUPLICATE`));
      }
    } else if (error instanceof ExceptionWithMessage) {
      useAlert(error.data);
    } else {
      useAlert(t(`${i18nPrefix}.ERROR_MESSAGE`));
    }
  }
};

const onClickViewDetails = async id => {
  const routeTypes = {
    contacts_dashboard_segments_index: ['contacts_edit_segment', 'segmentId'],
    contacts_dashboard_labels_index: ['contacts_edit_label', 'label'],
  };
  const [name, paramKey] = routeTypes[route.name] || ['contacts_edit'];
  const params = {
    contactId: id,
    ...(paramKey && { [paramKey]: route.params[paramKey] }),
  };

  await router.push({ name, params, query: route.query });
};

const toggleExpanded = id => {
  expandedCardId.value = expandedCardId.value === id ? null : id;
};

const isSelected = id => selectedIdsSet.value.has(id);

const selectedOnPage = computed(
  () => props.contacts.filter(contact => isSelected(contact.id)).length
);
const allOnPageSelected = computed(
  () =>
    props.contacts.length > 0 && selectedOnPage.value === props.contacts.length
);
const someOnPageSelected = computed(
  () => selectedOnPage.value > 0 && !allOnPageSelected.value
);

const handleSelect = (id, value) => {
  emit('toggleContact', { id, value });
};
</script>

<template>
  <div class="flex flex-col gap-2">
    <div
      class="hidden xl:grid-cols-[2rem_minmax(0,2.2fr)_minmax(0,2fr)_minmax(0,1.3fr)_minmax(0,1.2fr)_minmax(0,1.3fr)_6rem] items-center gap-x-4 rounded-xl border border-n-weak bg-n-solid-2 px-4 py-2 text-xs font-medium text-n-slate-11 xl:grid dark:border-n-strong"
      role="presentation"
    >
      <label
        class="flex size-8 items-center justify-center rounded-lg hover:bg-n-alpha-2"
      >
        <span class="sr-only">{{ t('CONTACTS_LAYOUT.LIST.SELECT_ALL') }}</span>
        <Checkbox
          :model-value="allOnPageSelected"
          :indeterminate="someOnPageSelected"
          @change="event => emit('toggleAll', event.target.checked)"
        />
      </label>
      <span>{{ t('CONTACTS_LAYOUT.LIST.COLUMNS.CONTACT') }}</span>
      <span>{{ t('CONTACTS_LAYOUT.LIST.COLUMNS.REACH') }}</span>
      <span>{{ t('CONTACTS_LAYOUT.LIST.COLUMNS.DOCUMENT') }}</span>
      <span>{{ t('CONTACTS_LAYOUT.LIST.COLUMNS.LAST_INTERACTION') }}</span>
      <span>{{ t('CONTACTS_LAYOUT.LIST.COLUMNS.PIPELINE') }}</span>
      <span class="text-right">
        {{ t('CONTACTS_LAYOUT.LIST.COLUMNS.ACTIONS') }}
      </span>
    </div>
    <ContactsCard
      v-for="contact in contacts"
      :id="contact.id"
      :key="contact.id"
      :name="contact.name"
      :email="contact.email"
      :company-id="contact.companyId"
      :thumbnail="contact.thumbnail"
      :phone-number="contact.phoneNumber"
      :additional-attributes="contact.additionalAttributes"
      :custom-attributes="contact.customAttributes"
      :talkvia-profile="contact.talkviaProfile"
      :talkvia-proposals="contact.talkviaProposals"
      :availability-status="contact.availabilityStatus"
      :last-activity-at="contact.lastActivityAt"
      :blacklisted="contact.blacklisted"
      :is-expanded="expandedCardId === contact.id"
      :is-updating="isUpdating"
      :is-selected="isSelected(contact.id)"
      @toggle="toggleExpanded(contact.id)"
      @update-contact="updateContact"
      @show-contact="onClickViewDetails"
      @select="value => handleSelect(contact.id, value)"
    />
  </div>
</template>
